import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { isInstalledPwa, isOffline, isOfflinePlayReady, onConnectivityChange } from '../services/pwa.js';

/**
 * A slim banner that appears on the home page when the browser goes offline. If offline play is ready
 * on this device (the engine is cached for the current app version — installed PWAs always are; a normal
 * tab is once it has opened /offline online) it offers a direct "Play offline" link. Otherwise it just
 * notes the offline state, since starting a game that isn't cached would fail. When online it renders
 * nothing. Connectivity (and readiness) are observed live so it appears/updates without a reload.
 */
@customElement('gc-offline-banner')
export class GcOfflineBanner extends LitElement {
  static override styles = css`
    :host { display: block; }
    .banner {
      display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap;
      margin: 0 0 1rem; padding: 0.7rem 1rem; border-radius: var(--gc-radius, 0.7rem);
      background: #fff4d6; border: 1px solid #e6cf86; color: #5a4a1a; font-size: 0.95rem;
    }
    .dot { width: 0.6rem; height: 0.6rem; border-radius: 50%; background: #d39b00; flex: none; }
    .label { font-weight: 600; }
    .cta {
      margin-left: auto; padding: 0.4rem 0.9rem; border-radius: 0.5rem; text-decoration: none;
      font-weight: 600; color: #fff; background: linear-gradient(135deg, #1f7d30, #2a9d3f);
    }
    .hint { margin-left: auto; color: #7a6a3a; }
  `;

  @state() private offline = isOffline();
  /** Whether the offline engine is actually cached for the current app version (the real SW marker). */
  @state() private ready = false;
  /** Whether we're running as an installed PWA (drives the "will be installed next launch" message). */
  @state() private installed = false;
  private unsubscribe?: () => void;

  override connectedCallback(): void {
    super.connectedCallback();
    this.offline = isOffline();
    void this.refreshReady();
    this.unsubscribe = onConnectivityChange((online) => {
      this.offline = !online;
      void this.refreshReady();
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
  }

  private async refreshReady(): Promise<void> {
    // Readiness must reflect whether the engine is genuinely cached — NOT merely that we're installed.
    // An installed PWA whose engine hasn't been cached yet (fresh install, or an app update that
    // invalidated the previous cache before the next online launch re-cached it) would otherwise be
    // wrongly offered "Play offline", which fails. Consult the real version-scoped marker.
    this.installed = isInstalledPwa();
    this.ready = await isOfflinePlayReady();
  }

  override render() {
    if (!this.offline) {
      return nothing;
    }
    return html`
      <div class="banner" role="status">
        <span class="dot" aria-hidden="true"></span>
        <span class="label">You're offline.</span>
        ${this.ready
          ? html`<span>You can still play against the computer.</span><a class="cta" href="/offline">Play offline</a>`
          : this.installed
            ? html`<span class="hint"
                >Offline play isn't ready yet — it'll be set up the next time you open the app while
                online.</span
              >`
            : html`<span class="hint">Reconnect to play online.</span>`}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gc-offline-banner': GcOfflineBanner;
  }
}
